import React, {FunctionComponent, useEffect, useMemo, useRef, useState} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import CustomTable, {CustomTableRef} from "../CustomTable";
import {Input} from "reactstrap";
import {CustomTableType, Data} from "../../../../interfaces/TableInterfaces";
import ModalModifyOrderItems from "../../modal/ModalModifyOrderItems";
import {OrderItemDefaultSelectable, OrderItemReferenceValue} from "../../../../interfaces/OrderItemInterfaces";
import {
  OrderBasicWithReferenceItemsDto,
  OrderBasicWithReferenceItemsSelectable
} from "../../../../interfaces/OrderInterfaces";
import OrderItemsFormTableRow from "./OrderItemsFormTableRow";
import OrderItemsFormTableRowDetails from "./OrderItemsFormTableRowDetails";
import ButtonRowSelectAction from "../ButtonRowSelectAction";
import Toggle from "../../../atoms/Toggle";
import Button from "../../../atoms/Button";

interface OrderItemsFormTableProps {
  className?: string,
  orders: OrderBasicWithReferenceItemsDto[],
  referenceType: OrderItemReferenceValue,
  setReferenceType?: (referenceType: OrderItemReferenceValue) => void,
  refresh?: () => void,
}

const OrderItemsFormTable: FunctionComponent<OrderItemsFormTableProps> = ({
  className,
  orders,
  referenceType,
  setReferenceType,
  refresh = () => null,
}) => {
  const intl = useIntl();
  const [modalOpen, setModalOpen] = useState(false);
  const [ordersSelectable, setOrdersSelectable] = useState<OrderBasicWithReferenceItemsSelectable[]>([]);
  const [selectedOrderItems, setSelectedOrderItems] = useState<OrderItemDefaultSelectable[]>([]);
  const atLeastOneActivitySelected = ordersSelectable.some(order => order.isSelected)
  const ref = useRef<CustomTableRef>();

  useEffect(() => {
    setOrdersSelectable(orders.map(orderItem => {
      return {
        ...orderItem,
        itemsWithReference: orderItem.itemsWithReference.map(orderItem => {
          return {
            ...orderItem,
            isSelected: false
          }
        }),
        isSelected: false
      }
    }))
  }, [orders]);

  const handleSelectAll = (allSelected: boolean) => {
    setOrdersSelectable(ordersSelectable.map(order => {
      return {
        ...order,
        itemsWithReference: order.itemsWithReference.map(orderItem => {
          return {
            ...orderItem,
            isSelected: !allSelected
          }
        }),
        isSelected: !allSelected
      }
    }))
  }

  const handleValidateModale = () => {
    setModalOpen(false);
    setSelectedOrderItems([]);
    setReferenceType(referenceType === OrderItemReferenceValue.DESCRIPTION ? OrderItemReferenceValue.PRICE : OrderItemReferenceValue.DESCRIPTION);
    refresh();
  }

  const handleValidateSelection = () => {
    setModalOpen(true);
    setSelectedOrderItems(
      ordersSelectable.map(order =>
        order.itemsWithReference
          .filter(orderItem => orderItem.isSelected)
          .map(orderItem => ({
            ...orderItem,
            orderName: order.customerReference
          }))
      ).flat()
    );  }

  const headerProps = {
    className: "epow-custom-table-header",
  }

  const columns = useMemo(() =>
    [
      {
        accessor: "name", title: "billing_table_header_name", ...headerProps,
        Cell: (<>
          <Input
            className="me-2"
            type="checkbox"
            checked={ordersSelectable.length > 0 && ordersSelectable.every(order => order.isSelected)}
            onChange={() => handleSelectAll(ordersSelectable.length > 0 && ordersSelectable.every(activity => activity.isSelected))}
          />
          <span>{intl.formatMessage({id: "billing_table_header_name"})}</span>
        </>),
        minWidth: "20%",
      },
      { accessor: "rendezVousDate", title: "order_item_date", ...headerProps, minWidth: "12%" },
      { accessor: "duration", title: "order_item_duration", ...headerProps, minWidth: "5%",},
      { accessor: "pax", title: "order_item_pax", ...headerProps, minWidth: "4%",},
      referenceType === OrderItemReferenceValue.DESCRIPTION ? { accessor: "description", title: "order_item_description", ...headerProps, minWidth: "59%"}
        : { accessor: "price", title: "order_item_pax_price", ...headerProps, minWidth: "59%"},
    ]
  , [orders, ordersSelectable])

  const buildOrderRow = (data: Data, open: boolean, rest) => {
    const order = data as OrderBasicWithReferenceItemsSelectable;
    return <div>
      <OrderItemsFormTableRow
        key={order.id}
        order={order}
        ordersSelectable={ordersSelectable}
        setOrdersSelectable={setOrdersSelectable}
        {...rest}
      />
      {open && (
        order.itemsWithReference.map((orderItem, indexOrderItem) => (
          <div
            key={`order_${order.id}_orderItem_${indexOrderItem}`}
            className={`rounded-0 ${indexOrderItem > 0 ? "border-top" : ""}`}
          >
            <OrderItemsFormTableRowDetails
              orderItem={orderItem}
              currentOrder={order}
              ordersSelectable={ordersSelectable}
              setOrdersSelectable={setOrdersSelectable}
              referenceType={referenceType}
            />
          </div>
        )
      ))}
      </div>
  }

  return (
    <div className={`epow-custom-table booking ${className ?? ""}`}>
      <div className="d-flex align-items-center justify-content-end ms-auto mb-3">
        <div className="d-flex align-items-center bold">
          <FormattedMessage id="table_see_all_details"/>
          <Toggle className="ms-2" id="order_all_details" onChange={(checked) => ref.current?.toggleAllRows(checked)}/>
        </div>
      </div>

      <CustomTable
        ref={ref}
        datas={ordersSelectable}
        type={CustomTableType.BOOKING}
        columns={columns}
        RowComponent={({data, open, ...rest}) => buildOrderRow(data, open, rest)}
      />

      {atLeastOneActivitySelected ? (
        <div className="d-flex justify-content-end">
          <ButtonRowSelectAction
            onClick={handleValidateSelection}
            numberOfElements={ordersSelectable.map(activity => activity.itemsWithReference.map(orderItem => orderItem.isSelected)).flat().filter(isSelected => isSelected).length}
            label="order_items_mass_update"
          />
        </div>
      ) : (
        <div className="d-flex justify-content-end">
          <Button
            className="mb-4"
            onClick={() => setReferenceType(referenceType === OrderItemReferenceValue.DESCRIPTION ? OrderItemReferenceValue.PRICE : OrderItemReferenceValue.DESCRIPTION)}
          >
            {intl.formatMessage({id: referenceType === OrderItemReferenceValue.DESCRIPTION ? "order_items_form_skip_description" : "order_items_form_skip_price"})}
          </Button>
        </div>
      )}
      <ModalModifyOrderItems
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onValidate={handleValidateModale}
        orderItems={selectedOrderItems}
        referenceType={referenceType}
      />
    </div>
  )
}

export default OrderItemsFormTable
